import { ChannelType } from './channels.js';

/**
 * A coluna de canais como uma lista só.
 *
 * O modelo é o do Discord e cabe numa frase: a categoria de um canal é a
 * última categoria acima dele; o que vem antes da primeira não tem categoria
 * nenhuma. Toda a manipulação da ordem — arrastar, largar dentro de uma
 * gaveta, tirar de lá — é a mesma operação sobre esta lista, e é por isso que
 * vive aqui e não dentro de um componente: é regra do produto, tem testes, e
 * o servidor valida o mesmo (ver `reorderChannelsSchema`).
 */

export interface ChannelLike {
  id: string;
  type: number;
  parentId: string | null;
  position: number;
}

export interface ChannelPlacement {
  id: string;
  position: number;
  parentId: string | null;
}

const byPosition = (a: ChannelLike, b: ChannelLike): number => a.position - b.position;

/** Pela ordem em que se vê: soltos primeiro, depois cada gaveta e o que tem. */
export function flattenChannels<T extends ChannelLike>(list: readonly T[]): T[] {
  const isCategory = (c: ChannelLike): boolean => c.type === ChannelType.GUILD_CATEGORY;
  const childrenOf = (id: string | null): T[] =>
    list.filter((c) => !isCategory(c) && (c.parentId ?? null) === id).sort(byPosition);

  const out: T[] = [...childrenOf(null)];
  for (const category of list.filter(isCategory).sort(byPosition)) {
    out.push(category, ...childrenOf(category.id));
  }
  return out;
}

/**
 * Onde tudo fica depois de largar `draggedId` em cima (ou por baixo) de
 * `targetId`.
 *
 * Devolve só o que MUDA — a lista inteira seria um pedido maior e um registo
 * de alterações cheio de linhas que ficaram na mesma.
 *
 * Uma categoria leva os filhos consigo: arrastar a gaveta e deixar lá dentro o
 * que estava não era mover nada.
 */
export function planChannelMove<T extends ChannelLike>(
  list: readonly T[],
  draggedId: string,
  targetId: string,
  before: boolean,
): ChannelPlacement[] {
  if (draggedId === targetId) return [];

  const flat = flattenChannels(list);
  const dragged = flat.find((c) => c.id === draggedId);
  if (!dragged || !flat.some((c) => c.id === targetId)) return [];

  const isCategory = (c: ChannelLike): boolean => c.type === ChannelType.GUILD_CATEGORY;
  const block = isCategory(dragged)
    ? flat.filter((c) => c.id === draggedId || c.parentId === draggedId)
    : [dragged];
  const moving = new Set(block.map((c) => c.id));

  // Largar uma gaveta em cima de um dos seus próprios filhos não é um sítio:
  // o filho sai da lista com ela e já não há onde a pôr.
  if (moving.has(targetId)) return [];

  const rest = flat.filter((c) => !moving.has(c.id));
  let at = rest.findIndex((c) => c.id === targetId);
  if (at < 0) at = rest.length;
  else if (!before) at += 1;

  const next = [...rest.slice(0, at), ...block, ...rest.slice(at)];

  let parent: string | null = null;
  const placements: ChannelPlacement[] = next.map((c, position) => {
    if (isCategory(c)) {
      parent = c.id;
      return { id: c.id, position, parentId: null };
    }
    return { id: c.id, position, parentId: parent };
  });

  return placements.filter((placement) => {
    const previous = list.find((c) => c.id === placement.id);
    if (!previous) return false;
    return (
      previous.position !== placement.position ||
      (previous.parentId ?? null) !== placement.parentId
    );
  });
}
